/**
 * ApproveModal — 480px review & commit. Summary stats, "approving as" user
 * card, graph_spec note, Approve & commit.
 */
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { colors, fontFamily, fontSize, fontWeight, radii } from '../theme';
import { Button, InternalBadge, Modal } from '../components';
import { useBridge } from '../store/bridgeStore';
import { useBuilder } from '../screens/builder/useBuilder';

export default function ApproveModal() {
  const { state, actions } = useBridge();
  const b = useBuilder();
  if (!state.approveOpen) return null;
  const sum = b.approveSummary;
  const u = state.currentUser;

  return (
    <Modal width={480} zIndex={70}>
      <View style={styles.head}>
        <Text style={styles.title}>Review &amp; approve mapping</Text>
        <Text className="t-mono" style={styles.sub}>{b.vendorName + ' · ' + b.selVer.catalog + ' × ' + b.selVer.vendor}</Text>
      </View>

      <View style={styles.body}>
        <View style={styles.stats}>
          <Stat big={sum.confidence} label="mean confidence" color={colors.accent} />
          <Stat big={sum.settled + '/' + sum.total} label="edges settled" color={colors.ink} />
          <Stat big={String(sum.resolved)} label="resolved by you" color={colors.success} />
        </View>

        <Text style={styles.approvingAs}>Approving as</Text>
        <View style={styles.userCard}>
          <View style={styles.userAvatar}><Text style={styles.userAvatarText}>{u.initials}</Text></View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={styles.userName}>{u.name}</Text>
            <Text className="t-mono" style={styles.userEmail}>{u.email}</Text>
          </View>
          <View style={styles.signedIn}><InternalBadge label="signed in" lock /></View>
        </View>

        <View style={styles.infoBox}>
          <Text style={styles.infoText}>
            On approval the mapping is committed as a deterministic <Text className="t-mono">graph_spec</Text> (draft → approved). The LLM never runs at request time.
          </Text>
        </View>
      </View>

      <View style={styles.footer}>
        <Button label="Cancel" variant="ghost" onPress={actions.closeApprove} />
        <Button label="Approve & commit" onPress={actions.approve} />
      </View>
    </Modal>
  );
}

function Stat({ big, label, color }: { big: string; label: string; color: string }) {
  return (
    <View style={styles.stat}>
      <Text style={[styles.statBig, { color } as any]}>{big}</Text>
      <Text style={styles.statLabel}>{label}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  head: { padding: 18, paddingHorizontal: 22, borderBottomWidth: 1, borderBottomColor: colors.line },
  title: { fontFamily: fontFamily.sans, fontWeight: fontWeight.semibold, fontSize: fontSize.lg, color: colors.ink },
  sub: { fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.ink3, marginTop: 2 },
  body: { padding: 22 },
  stats: { flexDirection: 'row', gap: 12, marginBottom: 20 },
  stat: { flex: 1, alignItems: 'center', padding: 14, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r3, backgroundColor: colors.paper2 },
  statBig: { fontFamily: fontFamily.sans, fontSize: fontSize.xxl, fontWeight: fontWeight.semibold },
  statLabel: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3, marginTop: 2 },
  approvingAs: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink2, fontWeight: fontWeight.medium, marginBottom: 6 },
  userCard: { flexDirection: 'row', alignItems: 'center', gap: 11, paddingVertical: 10, paddingHorizontal: 12, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r2, backgroundColor: colors.paper2 },
  userAvatar: { width: 34, height: 34, borderRadius: 17, backgroundColor: colors.accent, alignItems: 'center', justifyContent: 'center' },
  userAvatarText: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: '#fff' },
  userName: { fontFamily: fontFamily.sans, fontWeight: fontWeight.medium, fontSize: fontSize.md, color: colors.ink },
  userEmail: { fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.ink3 },
  signedIn: { },
  infoBox: { marginTop: 16, padding: 12, paddingHorizontal: 14, borderWidth: 1, borderColor: colors.infoLine, backgroundColor: colors.infoSoft, borderRadius: radii.r3 },
  infoText: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.info, lineHeight: 18 },
  footer: { padding: 14, paddingHorizontal: 22, borderTopWidth: 1, borderTopColor: colors.line, backgroundColor: colors.paper2, flexDirection: 'row', justifyContent: 'flex-end', gap: 10 },
});
